import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel from "@/components/feature/FilterPanel";
import Pagination from "@/components/feature/Pagination";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { filterMovies, sortMovies } from "@/utils/movieHelpers";

const PAGE_SIZE = 6;

const sortOptions = [
  { value: "updated", label: "按更新时间" },
  { value: "popular", label: "按热门程度" },
  { value: "rating", label: "按评分高低" },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const categoryType = type === "tv" ? "tv" : "movie";
  const isMovie = categoryType === "movie";

  const baseList = useMemo(
    () => movies.filter((m) => m.type === categoryType),
    [categoryType]
  );

  const regions = useMemo(
    () => Array.from(new Set(baseList.map((m) => m.region))),
    [baseList]
  );
  const years = useMemo(
    () =>
      Array.from(new Set(baseList.map((m) => m.year)))
        .sort((a, b) => b - a)
        .map(String),
    [baseList]
  );
  const genres = useMemo(
    () => Array.from(new Set(baseList.flatMap((m) => m.genres))),
    [baseList]
  );

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort("updated");
    setVisibleCount(PAGE_SIZE);
    setShowFilters(false);
  }, [categoryType]);

  const filtered = useMemo(
    () => sortMovies(filterMovies(baseList, { region, year, genre }), sort),
    [baseList, region, year, genre, sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(
    Math.max(1, Math.ceil(visibleCount / PAGE_SIZE)),
    totalPages
  );
  const visible = filtered.slice(0, visibleCount);

  const handleRegion = (v: string) => {
    setRegion(v);
    setVisibleCount(PAGE_SIZE);
  };
  const handleYear = (v: string) => {
    setYear(v);
    setVisibleCount(PAGE_SIZE);
  };
  const handleGenre = (v: string) => {
    setGenre(v);
    setVisibleCount(PAGE_SIZE);
  };
  const clearFilters = () => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setVisibleCount(PAGE_SIZE);
  };

  const title = isMovie ? "电影" : "电视剧";
  const seoTitle = `${title} - 肮脏的交易影视资讯`;
  const seoDescription = isMovie
    ? "电影频道提供最新电影资讯、剧情介绍和角色分析，涵盖动作、剧情、科幻、悬疑、爱情等主流类型。"
    : "电视剧频道提供最新剧集资讯、剧情介绍和角色分析，涵盖刑侦、都市、古装、医疗、奇幻等热门题材。";
  const introDescription = isMovie
    ? "电影频道提供最新电影资讯、剧情介绍和角色分析，涵盖动作、剧情、科幻、悬疑、爱情等主流类型。"
    : "电视剧频道提供最新剧集资讯、剧情介绍和角色分析，涵盖刑侦、都市、古装、医疗、奇幻等热门题材。";

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={`${title},影视资讯,${isMovie ? "电影推荐,电影剧情" : "剧集推荐,电视剧剧情"},肮脏的交易`}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: title }]} />
          <h1 className="mt-4 flex items-center gap-3 text-2xl font-bold text-foreground-950 md:text-3xl">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-600 text-background-50">
              <i className={isMovie ? "ri-film-line text-2xl" : "ri-tv-2-line text-2xl"} />
            </span>
            {title}频道
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {introDescription}
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr] lg:gap-8">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 lg:hidden cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-equalizer-line text-primary-600" />
                筛选与排序
              </span>
              <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </button>
            <div className={`${showFilters ? "mt-3 block" : "hidden"} lg:mt-0 lg:block`}>
              <FilterPanel
                regions={regions}
                years={years}
                genres={genres}
                region={region}
                year={year}
                genre={genre}
                onRegion={handleRegion}
                onYear={handleYear}
                onGenre={handleGenre}
                onClear={clearFilters}
              />
            </div>
          </aside>

          <div>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <strong className="text-primary-600">{filtered.length}</strong> 部{title}
              </p>
              <div className="flex items-center gap-2">
                <label htmlFor="sort" className="text-sm text-foreground-500 whitespace-nowrap">
                  排序：
                </label>
                <select
                  id="sort"
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-3xl" />
                </span>
                <h3 className="mt-4 text-base font-medium text-foreground-900">没有匹配的内容</h3>
                <p className="mt-2 text-sm text-foreground-500">
                  当前筛选条件下暂时没有找到{title}，试试调整或清空筛选条件。
                </p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-5 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer whitespace-nowrap"
                >
                  清空筛选条件
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
                  {visible.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                {visibleCount < filtered.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                      className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-lg" />
                      加载更多
                    </button>
                  </div>
                )}

                <div className="mt-8">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={(page) => {
                      setVisibleCount(page * PAGE_SIZE);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}